import { useState, useEffect } from 'react'
import { Sprout, Gift, Clock } from 'lucide-react'
import { useGameStore } from '../../store/gameStore'
import { useI18n } from '../../i18n/I18nProvider'

function FreeSeedClaim() {
  const { t } = useI18n()
  const canClaimFreeSeed = useGameStore((state) => state.canClaimFreeSeed)
  const getFreeSeedTimeRemaining = useGameStore((state) => state.getFreeSeedTimeRemaining)
  const claimFreeSeed = useGameStore((state) => state.claimFreeSeed)
  
  const [canClaim, setCanClaim] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [justClaimed, setJustClaimed] = useState(false)

  useEffect(() => {
    const updateStatus = () => {
      setCanClaim(canClaimFreeSeed())
      setTimeRemaining(getFreeSeedTimeRemaining())
    }

    updateStatus()
    const interval = setInterval(updateStatus, 1000)

    return () => clearInterval(interval)
  }, [canClaimFreeSeed, getFreeSeedTimeRemaining])

  const handleClaim = () => {
    const success = claimFreeSeed()
    if (success) {
      setJustClaimed(true)
      setTimeout(() => setJustClaimed(false), 3000)
    }
  }

  const formatTime = (ms) => {
    const hours = Math.floor(ms / 3600000)
    const minutes = Math.floor((ms % 3600000) / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`
    } else if (minutes > 0) {
      return `${minutes}min ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header avec icône */}
      <div className="bg-gradient-to-r from-green-400 to-green-500 p-8 flex items-center justify-center relative">
        <Sprout size={64} className="text-white" />
        {canClaim && (
          <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Gift size={12} />
            {t('freeSeedClaim.free')}
          </div>
        )}
      </div>
      
      {/* Contenu */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {t('freeSeedClaim.freeSeed')}
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {t('freeSeedClaim.description')}
        </p>
        
        {justClaimed && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-center text-sm font-bold animate-pulse">
            🎉 {t('freeSeedClaim.claimed')}
          </div>
        )}

        {canClaim ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sprout size={20} className="text-green-600" />
                <span className="text-xl font-bold text-green-600">{t('freeSeedClaim.free')}</span>
              </div>
            </div>
            
            <button
              onClick={handleClaim}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Gift size={20} />
              {t('freeSeedClaim.claim')}
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-gray-400" />
                <span className="text-sm text-gray-600">{t('freeSeedClaim.nextSeedIn')}</span>
              </div>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-4 text-center mb-4">
              <p className="text-2xl font-bold text-gray-700">{formatTime(timeRemaining)}</p>
            </div>
            
            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 font-bold py-3 px-6 rounded-lg cursor-not-allowed"
            >
              {t('freeSeedClaim.unavailable')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default FreeSeedClaim
